/**
 *
 * *****************************************
 * *** Matthew Rawson @ 09/08/2017 17:22 ***
 * *****************************************
 *
 * Helpers for markdown translation
 *
 * @notes: I was previously using markdown constants in here to keep things automated
 * however the regex is too complicated and seems to work differently when using string formats so
 * I have kept it all hardcoded regex for now.
 *
 */
import validator from "validator"
import {isArray, isString, isFunction} from "lodash"
import C from "../constants/markdown"

/**
 *
 * @param str
 * @returns {boolean}
 */
export const containsLinks = (str) =>
  (validator.matches(str, /(?:^|[^!])\[[^\]]+][^)]+\)/g))

/**
 *
 * @param str
 * @returns {boolean}
 */
export const containsImages = (str) =>
  (validator.matches(str, /!\[image][^)]+\)/ig))

/**
 *
 * @param str
 * @returns {boolean}
 */
export const containsHeadings = (str) =>
  (validator.matches(str, /(?:^[\s]*|\n{2})#{1,6}.*?(?:[\s]*$|\n{2})/g))

/**
 * @param str
 * @returns {boolean}
 */
export const containsHorizontalRules = (str) =>
  (validator.matches(str, /(?:^[\s]*|\n{2})-{3}(?:[\s]*$|[\s]*\n{2})/g))

/**
 * @param str
 * @returns {boolean}
 */
export const containsItalicStyling = (str) =>
  (validator.matches(str, /\b_((?:__|\\[\s\S]|[^\\_])+?)_\b|\*(?=\S)((?:\*\*|\s+(?:[^*\s]|\*\*)|[^\s*])+?)\*(?!\*)/g))

/**
 * @param str
 * @returns {boolean}
 */
export const containsBoldStyling = (str) =>
  (validator.matches(str, /\*{2}([\s\S]+?)\*{2}(?!$\*)/g))

/**
 *
 * @param str
 * @returns {boolean}
 */
const reContainsBulletPoints = /(?:^[\s]{0,3}|\n{2}[\s]{0,3})-[\s]+[\S]/g
export const containsBulletPoints = (str) =>
  (validator.matches(str, reContainsBulletPoints))

/**
 *
 * @param str
 * @returns {boolean}
 */
export const containsLegacyBulletPoints = (str) =>
  (validator.matches(str, /(?:^[\s]{0,3}|\n{2}[\s]{0,3})(?:\u2022|\*)[\s]+[\S]/g))


/**
 *
 * @param str
 * @returns {boolean}
 */
const reBulletPointList = /(?:^|\n{2})-[\s]+(.+)(?:\n[\s]*|[\s]*$)/gm
export const containsBulletPointList = (str) =>
  (validator.matches(str, reBulletPointList))

/**
 *
 * @param str
 * @returns {boolean}
 */
export const beginsWithBulletPointList = (str) =>
  (isString(str) && str.search(reContainsBulletPoints) === 0)


/**
 * @author Matthew Rawson @ 01/12/2017 15:38
 * @param str
 * @returns {boolean}
 */
export const containsHtml = (str) =>
  (validator.matches(str, /<[\S ]*[^ ]>/g))


/**
 * *** Matthew Rawson @ 10/08/2017 16:00 ***
 * Running .match with the m flag doesn't return the captured groups,
 * So i'm doing an extra step to strip the resulting list items.
 * If you can think of how to get the captured groups, let me know :)
 *
 * @param str
 * @returns {string[]} Array
 */
export const getBulletPointListItems = (str) => {
  let list = []
  if (isString(str)) {
    const matchedList = str.match(reBulletPointList)
    if (matchedList) {
      list = matchedList.map((bulletPoint) =>
        (bulletPoint.replace(C.BULLET_POINT_CHAR, "").replace(" ", "").replace(C.NEW_LINE_SEPARATOR, ""))
      )
    }
  }
  return list
}


/**
 * @param str
 * @returns {boolean}
 */
export const containsInvalidDoubleBulletPoints = (str) =>
  (validator.matches(str, /(?:^|\n)[ ]{0,3}-\s+-\s+(?:.+)/g))

export const regEx = {
  bulletPoint: new RegExp(C.BULLET_POINT_CHAR_FOR_REGEX, "g"),
  atLeastOneChar: new RegExp(`[${C.BULLET_POINT_CHAR_FOR_REGEX}](?!.)`, "g"),
  sequentialBulletChar: new RegExp(`[${C.BULLET_POINT_CHAR_FOR_REGEX}][${C.BULLET_POINT_CHAR_FOR_REGEX}]`, "g")
}

export const getListItemsFromMarkdown = (str) => {
  const re = new RegExp(`(?:^|[\r\n]+)[${C.BULLET_POINT_CHAR_FOR_REGEX}]`, "g")
  return isString(str) ? str.split(re).slice(1).map((aStr) => (aStr ? aStr.trim() : aStr)) : str
}


/**
 * *** Matthew Rawson @ 10/08/2017 11:12 ***
 * Contains error messages for each method
 *
 * @param func
 * @returns {*}
 * @private
 */
const _getErrorMessageForFunc = (func) => {
  switch (func) {
    case containsLinks:
      return "This field contains links."
    case containsImages:
      return "This field contains images."
    case containsHeadings:
      return "This field contains headings."
    case containsHorizontalRules:
      return "This field contains horizontal rules."
    case containsItalicStyling:
      return "This field contains italic styling."
    case containsBoldStyling:
      return "This field contains bold styling."
    case containsBulletPoints:
      return "This field contains bullet points."
    case containsLegacyBulletPoints:
      return "This field contains legacy bullet points."
    case containsBulletPointList:
      return "This field contains a bullet point list."
    case beginsWithBulletPointList:
      return "This field begins with a bullet point list."
    case containsHtml:
      return "This field contains html code."
    case containsInvalidDoubleBulletPoints:
      return "This field contains an invalid double bullet point."
    default:
      return "This field contains an error."
  }
}


/**
 * *** Matthew Rawson @ 10/08/2017 10:56 ***
 *
 * pass in a markdown string along with an array of validation methods.
 * Each validation method is assumed to return a boolean.
 *
 * If any funcs returns true trigger returning an error message.
 *
 * Future expansion could included the opposite of this to determine a str contains
 * all the required characters.
 *
 * Return only the first encountered error message.
 *
 *
 * @param str
 * @param validationFuncs
 * @returns {string}
 */
export const getErrorIfContains = (str, validationFuncs) => {
  if (!isArray(validationFuncs)) {
    throw new Error("Argument validationFuncs needs to be of array type.")
  }

  let errorMessage = null

  validationFuncs.forEach((func) => {
    if (!errorMessage && isFunction(func) && func(str)) {
      errorMessage = _getErrorMessageForFunc(func)
    }
  })

  return errorMessage
}
